import { useQuery } from '@apollo/client';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { useResource } from '@landsbankinn/l16n';
import { Column, device, useMedia } from '@landsbankinn/ui';
import { LandsbankinnContext } from '@landsbankinn/utils';
import React, { useContext } from 'react';
import type { fetchFunds } from '../../../graphql/__generated__/fetchFunds';
import { FETCH_FUNDS } from '../../../graphql/fetchFunds';
import { FundTypeInfoContainer } from '../fund/fund-type-information/FundTypeInformation';
import { MarketsDisclaimer } from '../shared/markets-disclaimer/MarketsDisclaimer';
import {
  ListHeaderContainer,
  MarketsBody,
  MarketsListTitle,
  MarketsListTitleMobile,
  MarketsSelectorTabs,
  MarketsSmall,
} from '../shared/MarketsComponents';
import { FundTable } from './fund-table/FundTable';
import res from './Funds.res';
import { ReturnsSelectorTab } from './returns-selector-tabs/ReturnsSelectorTabs';
import {
  AnnualizedReturnOptions,
  NominalReturnOptions,
} from './returns-selector-tabs/useReturnsConfig';

interface IProps {
  order?: CategoryOrder;
  funds?: fetchFunds;
  prefix?: string;
  disclaimerText: string;
  foreignCurrencyDisclaimer: string;
  einkabrefDisclaimer: string;
  fundsDisclaimer: string;
}

const TableContainer = styled.div<{ last?: boolean }>`
  margin-bottom: 65px;
  ${(props) => (props.last ? 'margin-bottom:0;' : '')};
  @media ${device.tablet} {
    margin-bottom: 60px;
    ${(props) => (props.last ? 'margin-bottom:0;' : '')};
  }
`;

const FootnoteContainer = styled.div`
  padding-top: 8px;
  display: flex;
  justify-content: flex-start;
`;

const Disclaimer = styled.div<{ marginTop?: boolean }>`
  padding-top: 10px;
  ${(props) => (props.marginTop ? 'margin-top:50px;' : '')};
`;

export function Funds({
  order = CategoryOrder.Personal,
  funds,
  prefix,
  disclaimerText,
  foreignCurrencyDisclaimer,
  einkabrefDisclaimer,
  fundsDisclaimer,
}: IProps) {
  const { r } = useResource();
  const isMobile = useMedia('sm');
  const isTablet = useMedia('md');
  const theme = useTheme();

  const { client } = useContext(LandsbankinnContext);
  const { data = funds } = useQuery<fetchFunds>(FETCH_FUNDS, {
    client,
    variables: { first: 100 },
  });

  const [fundDataType, setSelectedFundDataType] = React.useState(
    FundDataType.NominalReturn,
  );

  const categories = data?.funds.nodes
    .map((category) => category.category)
    .filter((value, index, self) => self.indexOf(value) === index);

  if (order === CategoryOrder.Corporate) {
    categories?.forEach(function (item, i) {
      if (item === 'BondFund' && i >= 1) {
        [categories[0], categories[i]] = [categories[i], categories[0]];
      }
    });
  }

  const changeDataType = (fundDataType: FundDataType) => {
    setSelectedFundDataType(fundDataType);
  };

  const [selectedNominalReturn, setSelectedNominalReturn] = React.useState(
    NominalReturnOptions.YearToDateReturn,
  );
  const [selectedAnnualizedReturn, setSelectedAnnualizedReturn] =
    React.useState(AnnualizedReturnOptions.OneYear);

  return (
    <>
      <ListHeaderContainer>
        <Column md={{ width: 4 / 12 }} lg={{ width: 5 / 12 }}>
          {!isMobile ? (
            <>
              <MarketsListTitle>{r(res.funds)}</MarketsListTitle>
              <MarketsSmall>{r(res.dataDisclaimer)}</MarketsSmall>
            </>
          ) : (
            <MarketsListTitleMobile>{r(res.fundPrice)}</MarketsListTitleMobile>
          )}
        </Column>
        <Column md={{ width: 5 / 12 }} lg={{ width: 5 / 12 }}>
          <MarketsSelectorTabs
            mobileLabel={r(res.selectInformation)}
            mobileInset
            onSelectTab={(val) => {
              changeDataType(Number(val));
            }}
            selectedItemId={fundDataType.toString()}
            items={[
              {
                id: FundDataType.NominalReturn.toString(),
                label: r(res.nominalReturn),

                width: isTablet ? 4.5 : 3.5,
              },
              {
                id: FundDataType.AnnualizedReturn.toString(),
                label: r(res.nominalReturnAnnualized),
                width: isTablet ? 4.5 : 6.5,
              },
              {
                id: FundDataType.Price.toString(),
                label: r(res.rate),

                width: isTablet ? 3 : 2,
              },
            ]}
            color={theme.colors.secondary}
          />
        </Column>
        {isMobile && fundDataType !== FundDataType.Price && (
          <Column md={{ width: 8 / 12 }} lg={{ width: 7 / 12 }}>
            <ReturnsSelectorTab
              fundDataType={fundDataType}
              initialNominalReturn={selectedNominalReturn}
              initialAnnualizedReturn={selectedAnnualizedReturn}
              onSelectNominalReturn={(id) => {
                setSelectedNominalReturn(id);
              }}
              onSelectAnnualizedReturn={(id) => {
                setSelectedAnnualizedReturn(id);
              }}
            />
          </Column>
        )}
      </ListHeaderContainer>

      {categories?.map((category, index) => (
        <TableContainer
          key={category}
          last={categories.length - 1 === index ? true : false}
        >
          <FundTable
            category={category}
            data={
              data?.funds.nodes.filter((fund) => fund.category === category) ??
              []
            }
            fundDataType={fundDataType}
            selectedNominalReturn={selectedNominalReturn}
            selectedAnnualizedReturn={selectedAnnualizedReturn}
            prefix={prefix}
          />
          {category == 'EquityFund' && (
            <FootnoteContainer>
              <MarketsSmall>{foreignCurrencyDisclaimer}</MarketsSmall>
            </FootnoteContainer>
          )}
          {category == 'PrivateBankingFund' && (
            <>
              <FootnoteContainer>
                <MarketsSmall>{einkabrefDisclaimer}</MarketsSmall>
              </FootnoteContainer>
              <FootnoteContainer>
                <MarketsSmall>{r(res.nonUcitsDisclaimer)}</MarketsSmall>
              </FootnoteContainer>
              <FootnoteContainer>
                <MarketsSmall>{r(res.fundsDisclaimer)}</MarketsSmall>
              </FootnoteContainer>
            </>
          )}
        </TableContainer>
      ))}
      <Disclaimer>
        <MarketsSmall>{fundsDisclaimer}</MarketsSmall>
      </Disclaimer>

      <MarketsDisclaimer text={disclaimerText} />
      <FundTypeInfoContainer>{r(res.fundsDisclaimer2)}</FundTypeInfoContainer>
    </>
  );
}

export enum FundDataType {
  AnnualizedReturn,
  NominalReturn,
  Price,
}

export enum CategoryOrder {
  Personal = 'Personal',
  Corporate = 'Corporate',
}
